import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Drawer,
  Dropdown,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from "antd"
import { MoreOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import { useSelector } from "react-redux"
import PanelLayout from "../../Layout/PanelLayout"
import SelectBox from "../../components/SelectBox/SelectBox"
import apiClient from "../../Utils/apiClient"
import { assetUrl } from "../../config"
import "../Operations/Operations.scss"

const { TextArea } = Input

const STAFF_ROLES = new Set(["network-admin", "admin", "co-admin"])

const canCreateBroadcasts = (userInfo) => {
  const role = String(userInfo?.role || "").trim().toLowerCase()
  if (STAFF_ROLES.has(role)) return true
  const permissions = Array.isArray(userInfo?.permissions)
    ? userInfo.permissions
    : []
  return permissions.includes("*") || permissions.includes("create-broadcasts")
}
const REGIONS = [
  { value: "EMEA", label: "EMEA" },
  { value: "NLAM", label: "NLAM (NL + Americas)" },
  { value: "APAC", label: "APAC" },
]

const TIMEZONES = [
  "Asia/Kolkata",
  "Asia/Dubai",
  "Europe/Amsterdam",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "UTC",
]

const CRON_PRESETS = [
  { value: "0 9 * * 1-5", label: "Weekdays 09:00" },
  { value: "0 18 * * 1-5", label: "Weekdays 18:00" },
  { value: "0 9 * * 1", label: "Mondays 09:00" },
  { value: "0 15 * * 5", label: "Fridays 15:00" },
  { value: "0 12 * * *", label: "Daily 12:00" },
]

const CRON_CUSTOM = "__custom__"
const CRON_OPTIONS = [
  ...CRON_PRESETS,
  { value: CRON_CUSTOM, label: "Custom…" },
]

const isKnownCronPreset = (cron) =>
  CRON_PRESETS.some((item) => item.value === String(cron || "").trim())

const cronLabel = (cron) =>
  CRON_PRESETS.find((item) => item.value === cron)?.label ||
  (cron ? `Custom · ${cron}` : "recurring")

const looksLikeCron = (value) => {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
  return parts.length === 5 && parts.every(Boolean)
}

/** Preset select + optional freeform cron when Custom is chosen. */
const CronScheduleFields = ({ form, namePath }) => {
  const cronValue = Form.useWatch(namePath, form)
  const presetValue = isKnownCronPreset(cronValue) ? cronValue : CRON_CUSTOM
  return (
    <>
      <Form.Item
        label="Repeat"
        required
        style={{ marginBottom: presetValue === CRON_CUSTOM ? 8 : undefined }}
      >
        <Select
          options={CRON_OPTIONS}
          value={presetValue}
          onChange={(value) => {
            if (value === CRON_CUSTOM) {
              form.setFieldValue(
                namePath,
                isKnownCronPreset(cronValue) ? "" : cronValue || ""
              )
            } else {
              form.setFieldValue(namePath, value)
            }
          }}
        />
      </Form.Item>
      {presetValue === CRON_CUSTOM ? (
        <Form.Item
          name={namePath}
          label="Custom cron"
          extra="Five fields: minute hour day-of-month month day-of-week (e.g. 30 14 * * 1-5)"
          rules={[
            { required: true, message: "Enter a cron expression" },
            {
              validator: (_, value) =>
                looksLikeCron(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error("Use a 5-field cron expression")),
            },
          ]}
        >
          <Input placeholder="0 9 * * 1-5" />
        </Form.Item>
      ) : (
        <Form.Item name={namePath} hidden rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      )}
    </>
  )
}
const scheduleLabel = (row) => {
  const mode = row?.schedule?.mode || "now"
  if (mode === "recurring") {
    return `Recurring · ${cronLabel(row.schedule?.cron)}`
  }
  if (mode === "once" && row?.schedule?.sendAt) {
    return `Once · ${dayjs(row.schedule.sendAt).format("DD MMM, HH:mm")}`
  }
  return "Send now"
}

const modeLabel = (value) => {
  if (value === "ack") return "Must ack"
  if (value === "lockscreen") return "Lock / idle"
  return "Toast"
}

const statusColor = (status) => {
  const value = String(status || "").toLowerCase()
  if (value === "sending" || value === "scheduled") return "processing"
  if (value === "sent" || value === "completed") return "success"
  if (value === "cancelled" || value === "failed") return "error"
  if (value === "draft") return "default"
  return "blue"
}

const BROADCAST_VIEWS = [
  { key: "all", label: "All broadcasts", tone: "#2563eb", soft: "rgba(59, 130, 246, 0.14)" },
  { key: "draft", label: "Drafts", tone: "#64748b", soft: "rgba(148, 163, 184, 0.12)" },
  { key: "scheduled", label: "Scheduled / sending", tone: "#0891b2", soft: "rgba(56, 189, 248, 0.14)" },
  { key: "sent", label: "Sent", tone: "#059669", soft: "rgba(34, 197, 94, 0.14)" },
  { key: "stopped", label: "Cancelled / failed", tone: "#dc2626", soft: "rgba(248, 113, 113, 0.14)" },
]

const MODE_CHIPS = [
  { value: "", label: "All" },
  { value: "toast", label: "Toast" },
  { value: "ack", label: "Must ack" },
  { value: "lockscreen", label: "Lock / idle" },
]

const broadcastBucket = (status) => {
  const value = String(status || "").toLowerCase()
  if (value === "draft") return "draft"
  if (value === "sending" || value === "scheduled") return "scheduled"
  if (value === "sent" || value === "completed") return "sent"
  if (value === "cancelled" || value === "failed") return "stopped"
  return "scheduled"
}

const DesktopBroadcasts = () => {
  const userInfo = useSelector((state) => state.user.data)
  const [view, setView] = useState("all")
  const [modeFilter, setModeFilter] = useState("")
  const canCreate = useMemo(() => canCreateBroadcasts(userInfo), [userInfo])
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [detail, setDetail] = useState(null)
  const [editingId, setEditingId] = useState("")
  const [selectedIds, setSelectedIds] = useState([])
  const [scheduleTarget, setScheduleTarget] = useState(null)
  const [scheduleForm] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [media, setMedia] = useState({ type: "none", url: "", mime: "", fileName: "" })
  const [groupId, setGroupId] = useState([])
  const [deviceIds, setDeviceIds] = useState([])
  const [aiPrompt, setAiPrompt] = useState("")
  const [drafting, setDrafting] = useState(false)
  const targetType = Form.useWatch("targetType", form)
  const scheduleMode = Form.useWatch(["schedule", "mode"], form)
  const surveyEnabled = Form.useWatch(["survey", "enabled"], form)
  const scheduleModalMode = Form.useWatch("mode", scheduleForm)
  const previewTitle = Form.useWatch("title", form)
  const previewBody = Form.useWatch("body", form)
  const previewMode = Form.useWatch("displayMode", form)

  const load = async () => {
    setLoading(true)
    try {
      const res = await apiClient("broadcasts", "GET")
      if (res?.success) setRows(res.result || [])
    } catch (err) {
      message.error(err.message || "Could not load broadcasts")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openCompose = () => {
    if (!canCreate) {
      message.warning("Your role cannot create desktop broadcasts")
      return
    }    form.resetFields()
    form.setFieldsValue({
      urgency: "info",
      displayMode: "toast",
      targetType: "all",
      schedule: { mode: "now", timezone: "Asia/Kolkata", cron: "0 9 * * 1-5" },
      survey: { enabled: false, question: "", choices: ["Yes", "No"] },
    })
    setGroupId([])
    setDeviceIds([])
    setMedia({ type: "none", url: "", mime: "", fileName: "" })
    setAiPrompt("")
    setEditingId("")
    setDrawerOpen(true)
    setDetail(null)
  }

  const fillForm = (row) => {
    const groups = (row.groups || []).map((item) => String(item))
    const devices = (row.devices || []).map((item) => String(item))
    setGroupId(groups)
    setDeviceIds(devices)
    setMedia(
      row.media && row.media.type && row.media.type !== "none"
        ? {
            type: row.media.type,
            url: row.media.url || "",
            mime: row.media.mime || "",
            fileName: row.media.fileName || "",
          }
        : { type: "none", url: "", mime: "", fileName: "" }
    )
    form.setFieldsValue({
      title: row.title || "",
      body: row.body || "",
      urgency: row.urgency || "info",
      displayMode: row.displayMode || "toast",
      targetType: row.targetType || "all",
      groups,
      devices,
      regions: row.regions || [],
      deviceStatuses: row.deviceStatuses || [],
      survey: {
        enabled: Boolean(row.survey?.enabled),
        question: row.survey?.question || "",
      },
      surveyChoices: (row.survey?.choices || ["Yes", "No"]).join(", "),
      schedule: {
        mode: row.schedule?.mode || "now",
        sendAt: row.schedule?.sendAt ? dayjs(row.schedule.sendAt) : null,
        cron: row.schedule?.cron || "0 9 * * 1-5",
        timezone: row.schedule?.timezone || "Asia/Kolkata",
      },
    })
  }

  const openEdit = async (id) => {
    setLoading(true)
    try {
      const res = await apiClient(`broadcasts/${id}`, "GET")
      if (!res?.success) {
        throw new Error(res?.message || "Could not load broadcast")
      }
      const row = res.result
      setDetail(row)
      setEditingId(String(row._id))
      setAiPrompt("")
      fillForm(row)
      setDrawerOpen(true)
    } catch (err) {
      message.error(err.message || "Could not load broadcast")
    } finally {
      setLoading(false)
    }
  }

  const draftWithAi = async () => {
    const prompt = aiPrompt.trim()
    if (!prompt) {
      message.warning("Describe the notice first")
      return
    }
    setDrafting(true)
    try {
      const res = await apiClient("assistant/draft-broadcast", "POST", {
        prompt,
        kind: form.getFieldValue("displayMode") || "toast",
      })
      if (res?.offline || !res?.success) {
        throw new Error(res?.message || "local model offline")
      }
      form.setFieldsValue({
        title: res.title || form.getFieldValue("title"),
        body: res.body || form.getFieldValue("body"),
      })
      message.success("Draft filled — review before sending")
    } catch (err) {
      message.error(err.message || "Could not draft broadcast")
    } finally {
      setDrafting(false)
    }
  }

  const openDetail = async (id) => {
    setLoading(true)
    try {
      const res = await apiClient(`broadcasts/${id}`, "GET")
      if (res?.success) {
        setDetail(res.result)
        setEditingId("")
        setDrawerOpen(true)
      }
    } catch (err) {
      message.error(err.message || "Could not load broadcast")
    } finally {
      setLoading(false)
    }
  }

  const uploadMedia = async (file) => {
    const body = new FormData()
    body.append("file", file)
    try {
      const res = await apiClient("broadcasts/media", "POST", body)
      if (res?.success) {
        setMedia(res.result)
        message.success("Media attached")
      } else {
        message.error(res?.message || "Upload failed")
      }
    } catch (err) {
      message.error(err.message || "Upload failed")
    }
    return false
  }

  const buildPayload = (values, sendNow = false) => {
    const choices = String(values.surveyChoices || "Yes,No")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
    return {
      title: values.title?.trim(),
      body: values.body || "",
      urgency: values.urgency,
      displayMode: values.displayMode,
      media,
      survey: {
        enabled: Boolean(values.survey?.enabled),
        question: values.survey?.question || "",
        choices,
      },
      targetType: values.targetType,
      groups: values.targetType === "group" ? groupId || [] : [],
      devices: values.targetType === "specific" ? deviceIds || [] : [],
      regions: values.targetType === "region" ? values.regions || [] : [],
      deviceStatuses:
        values.targetType === "specific" ? [] : values.deviceStatuses || [],
      schedule: {
        mode: values.schedule?.mode || "now",
        sendAt:
          values.schedule?.mode === "once" && values.schedule?.sendAt
            ? values.schedule.sendAt.toISOString()
            : null,
        cron: values.schedule?.mode === "recurring" ? values.schedule?.cron || "" : "",
        timezone: values.schedule?.timezone || "Asia/Kolkata",
      },
      sendNow,
    }
  }

  const submit = async (sendNow = false) => {
    const values = await form.validateFields()
    setLoading(true)
    try {
      const payload = buildPayload(values, sendNow)
      const id = editingId
      const res = id
        ? await apiClient(`broadcasts/${id}`, "PATCH", payload)
        : await apiClient("broadcasts", "POST", payload)
      if (res?.success) {
        message.success(res.message)
        if (id) {
          setEditingId("")
          await openDetail(id)
        } else {
          setDrawerOpen(false)
          setDetail(null)
        }
        load()
      } else {
        message.error(res?.message || (editingId ? "Could not update" : "Could not send"))
      }
    } catch (err) {
      message.error(err.message || (editingId ? "Could not update broadcast" : "Could not send broadcast"))
    } finally {
      setLoading(false)
    }
  }

  const cancel = async (id) => {
    setLoading(true)
    try {
      const res = await apiClient(`broadcasts/${id}/cancel`, "POST", {})
      if (res?.success) {
        message.success(res.message)
        load()
        if (detail?._id === id) setDetail(res.result)
      } else {
        message.error(res?.message || "Cancel failed")
      }
    } catch (err) {
      message.error(err.message || "Cancel failed")
    } finally {
      setLoading(false)
    }
  }

  const remove = (id, title) => {
    Modal.confirm({
      title: "Delete this broadcast?",
      content: title
        ? `“${title}” will be removed permanently, including delivery history.`
        : "This broadcast and its delivery history will be removed permanently.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Keep",
      onOk: async () => {
        setLoading(true)
        try {
          const res = await apiClient(`broadcasts/${id}`, "DELETE")
          if (res?.success) {
            message.success(res.message || "Broadcast deleted")
            if (detail?._id === id) {
              setDrawerOpen(false)
              setDetail(null)
            }
            await load()
          } else {
            message.error(res?.message || "Delete failed")
          }
        } catch (err) {
          message.error(err.message || "Delete failed")
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const sendNow = async (id, { open = false } = {}) => {
    setLoading(true)
    try {
      const res = await apiClient(`broadcasts/${id}/send`, "POST", {})
      if (res?.success) {
        message.success(res.message)
        if (open) await openDetail(id)
        load()
      } else {
        message.error(res?.message || "Send failed")
      }
    } catch (err) {
      message.error(err.message || "Send failed")
    } finally {
      setLoading(false)
    }
  }

  const sendSelected = async () => {
    if (!selectedIds.length) {
      message.warning("Select a broadcast first")
      return
    }
    setLoading(true)
    try {
      const res = await apiClient("broadcasts/send-selected", "POST", {
        ids: selectedIds,
      })
      if (res?.success) {
        message.success(res.message)
        setSelectedIds([])
        load()
      } else {
        message.error(res?.message || "Send failed")
      }
    } catch (err) {
      message.error(err.message || "Send failed")
    } finally {
      setLoading(false)
    }
  }

  const openSchedule = (row) => {
    setScheduleTarget(row)
    scheduleForm.setFieldsValue({
      mode: row?.schedule?.mode === "now" ? "once" : row?.schedule?.mode || "once",
      sendAt: row?.schedule?.sendAt ? dayjs(row.schedule.sendAt) : dayjs().add(1, "hour"),
      cron: row?.schedule?.cron || "0 9 * * 1-5",
      timezone: row?.schedule?.timezone || "Asia/Kolkata",
      sendNow: false,
    })
  }

  const saveSchedule = async () => {
    const values = await scheduleForm.validateFields()
    if (!scheduleTarget?._id) return
    setLoading(true)
    try {
      const res = await apiClient(`broadcasts/${scheduleTarget._id}/schedule`, "POST", {
        schedule: {
          mode: values.mode,
          sendAt:
            values.mode === "once" && values.sendAt
              ? values.sendAt.toISOString()
              : null,
          cron: values.mode === "recurring" ? values.cron || "" : "",
          timezone: values.timezone || "Asia/Kolkata",
        },
        sendNow: Boolean(values.sendNow),
      })
      if (res?.success) {
        message.success(res.message)
        setScheduleTarget(null)
        load()
        if (detail?._id === scheduleTarget._id) setDetail(res.result)
      } else {
        message.error(res?.message || "Could not save schedule")
      }
    } catch (err) {
      message.error(err.message || "Could not save schedule")
    } finally {
      setLoading(false)
    }
  }

  const broadcastSummary = useMemo(() => {
    const summary = { all: rows.length, draft: 0, scheduled: 0, sent: 0, stopped: 0 }
    rows.forEach((row) => {
      summary[broadcastBucket(row.status)] += 1
    })
    return summary
  }, [rows])

  const visibleRows = useMemo(
    () =>
      rows.filter((row) => {
        if (view !== "all" && broadcastBucket(row.status) !== view) return false
        if (modeFilter && row.displayMode !== modeFilter) return false
        return true
      }),
    [rows, view, modeFilter]
  )

  const composing = !detail || Boolean(editingId)
  const canSendAgain = detail?.status !== "cancelled"

  return (
    <PanelLayout
      title="Desktop broadcasts"
      subtitle="Toast, acknowledge, or lock-screen notices to managed PCs."
      actions={
        canCreate ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCompose}>
            New broadcast
          </Button>
        ) : null
      }
    >
      <div className="ops-kpis">
        {BROADCAST_VIEWS.map((card) => (
          <button
            type="button"
            key={card.key}
            className={`ops-kpi${view === card.key ? " is-active" : ""}`}
            style={{ "--act-tone": card.tone, "--act-soft": card.soft }}
            onClick={() => setView(card.key)}
          >
            <div>
              <span className="ops-kpi-label">{card.label}</span>
              <strong>{broadcastSummary[card.key] ?? 0}</strong>
            </div>
          </button>
        ))}
      </div>

      <Card className="ops-card broadcast-card" loading={loading && !rows.length}>
        <div className="ops-toolbar broadcast-toolbar">
          <span className="ops-toolbar__count">
            {selectedIds.length
              ? `${selectedIds.length} selected`
              : `${visibleRows.length} broadcast${visibleRows.length === 1 ? "" : "s"}${
                  view !== "all"
                    ? ` · ${BROADCAST_VIEWS.find((c) => c.key === view)?.label}`
                    : ""
                }`}
          </span>
          <div className="dashboard-filter-chips">
            <span>Mode</span>
            {MODE_CHIPS.map((chip) => (
              <Button
                key={chip.value || "all-mode"}
                size="small"
                type={modeFilter === chip.value ? "primary" : "default"}
                onClick={() => setModeFilter(chip.value)}
              >
                {chip.label}
              </Button>
            ))}
          </div>
          {canCreate ? (
            <>
            <Button
              type="primary"
              disabled={!selectedIds.length}
              loading={loading}
              onClick={sendSelected}
            >
              Send now
            </Button>
            <Button
              disabled={selectedIds.length !== 1}
              onClick={() => {
                const row = rows.find(
                  (item) => String(item._id) === String(selectedIds[0])
                )
                if (row) openSchedule(row)
                else message.warning("Select one broadcast to schedule")
              }}
            >
              Schedule
            </Button>
            </>
          ) : null}
        </div>
        <Table
          className="broadcast-table"
          rowKey="_id"
          size="middle"
          dataSource={visibleRows}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 960 }}
          rowSelection={
            canCreate
              ? {
                  selectedRowKeys: selectedIds,
                  onChange: setSelectedIds,
                  columnWidth: 48,
                }
              : undefined
          }
          columns={[
            {
              title: "Title",
              dataIndex: "title",
              ellipsis: true,
              render: (title, row) => (
                <Tooltip title={title || ""} placement="topLeft">
                  <button
                    type="button"
                    className="broadcast-title"
                    onClick={() => openDetail(row._id)}
                  >
                    <span className="broadcast-title__name">
                      {title || "Untitled"}
                    </span>
                    {row.urgency === "critical" ? (
                      <Tag color="red" className="broadcast-title__tag">
                        Critical
                      </Tag>
                    ) : null}
                  </button>
                </Tooltip>
              ),
            },
            {
              title: "Mode",
              dataIndex: "displayMode",
              width: 110,
              render: (value) => (
                <Tag className="broadcast-pill">{modeLabel(value)}</Tag>
              ),
            },
            {
              title: "Target",
              dataIndex: "targetType",
              width: 120,
              ellipsis: true,
              render: (value, row) =>
                value === "region"
                  ? (row.regions || []).join(", ") || "region"
                  : value || "—",
            },
            {
              title: "Schedule",
              width: 170,
              ellipsis: true,
              render: (_, row) => (
                <span className="broadcast-muted">{scheduleLabel(row)}</span>
              ),
            },
            {
              title: "Status",
              dataIndex: "status",
              width: 110,
              render: (value) => (
                <Tag color={statusColor(value)}>
                  {String(value || "—").replace(/^\w/, (c) => c.toUpperCase())}
                </Tag>
              ),
            },
            {
              title: "Delivered",
              width: 110,
              align: "right",
              render: (_, row) => (
                <span className="broadcast-counts">
                  {row.counts?.delivered || 0}
                  <span className="broadcast-muted">
                    {" "}
                    / {row.counts?.acked || 0}
                  </span>
                </span>
              ),
            },
            {
              title: "",
              key: "actions",
              width: canCreate ? 120 : 88,
              fixed: "right",
              render: (_, row) => {
                const menuItems = []
                if (canCreate) {
                  menuItems.push({
                    key: "edit",
                    label: "Edit",
                    onClick: () => openEdit(row._id),
                  })
                  if (row.status !== "cancelled") {
                    menuItems.push(
                      {
                        key: "send",
                        label: "Send now",
                        onClick: () => sendNow(row._id),
                      },
                      {
                        key: "schedule",
                        label: "Schedule",
                        onClick: () => openSchedule(row),
                      },
                      {
                        key: "cancel",
                        label: "Cancel",
                        danger: true,
                        onClick: () => cancel(row._id),
                      }
                    )
                  }
                  menuItems.push({
                    key: "delete",
                    label: "Delete",
                    danger: true,
                    onClick: () => remove(row._id, row.title),
                  })
                }
                return (
                  <Space size={4} className="broadcast-actions">
                    <Button type="link" size="small" onClick={() => openDetail(row._id)}>
                      Details
                    </Button>
                    {menuItems.length ? (
                      <Dropdown
                        menu={{ items: menuItems }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <Button
                          type="text"
                          size="small"
                          icon={<MoreOutlined />}
                          aria-label="Broadcast actions"
                        />
                      </Dropdown>
                    ) : null}
                  </Space>
                )
              },
            },
          ]}
        />
      </Card>

      <Drawer
        width={640}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setDetail(null)
          setEditingId("")
        }}
        title={
          editingId
            ? "Edit desktop broadcast"
            : composing
              ? "Compose desktop broadcast"
              : detail?.title
        }
        extra={
          composing ? (
            <Space>
              {editingId ? (
                <>
                  <Button onClick={() => openDetail(editingId)}>Back</Button>
                  {scheduleMode === "now" ? (
                    <>
                      {editingId ? (
                        <Button loading={loading} onClick={() => submit(false)}>
                          Save changes
                        </Button>
                      ) : null}
                      <Button type="primary" loading={loading} onClick={() => submit(true)}>
                        {editingId ? "Save and send" : "Send now"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button loading={loading} onClick={() => submit(false)}>
                        Save schedule
                      </Button>
                      <Button type="primary" loading={loading} onClick={() => submit(true)}>
                        Send now and keep schedule
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <Space>
                  {scheduleMode === "now" ? (
                    <Button type="primary" loading={loading} onClick={() => submit(true)}>
                      Send now
                    </Button>
                  ) : (
                    <>
                      <Button loading={loading} onClick={() => submit(false)}>
                        Save schedule
                      </Button>
                      <Button type="primary" loading={loading} onClick={() => submit(true)}>
                        Send now and keep schedule
                      </Button>
                    </>
                  )}
                </Space>
              )}
            </Space>
          ) : (
            <Space>
              {canCreate ? (
                <>
                  <Button onClick={() => openEdit(detail._id)}>Edit</Button>
                  {canSendAgain && (
                    <Button type="primary" onClick={() => sendNow(detail._id)}>
                      Send now
                    </Button>
                  )}
                  {canSendAgain && (
                    <Button onClick={() => openSchedule(detail)}>Schedule</Button>
                  )}
                  {canSendAgain && (
                    <Button danger onClick={() => cancel(detail._id)}>
                      Cancel
                    </Button>
                  )}
                  <Button danger onClick={() => remove(detail._id, detail.title)}>
                    Delete
                  </Button>
                </>
              ) : null}
            </Space>
          )
        }
      >
        {composing ? (
          <Form form={form} layout="vertical">
            {editingId ? (
              <Alert
                className="mb-3"
                type="info"
                showIcon
                message="Editing an existing notice"
                description="Save updates this broadcast for the next send. PCs that already received it keep the old popup until you use Save and send or Send again."
              />
            ) : null}
            <Form.Item label="Draft with AI (local)">
              <Space.Compact style={{ width: "100%" }}>
                <Input
                  value={aiPrompt}
                  onChange={(event) => setAiPrompt(event.target.value)}
                  placeholder="e.g. remind APAC to shut down by 6pm"
                  maxLength={400}
                />
                <Button loading={drafting} onClick={draftWithAi}>
                  Draft
                </Button>
              </Space.Compact>
            </Form.Item>
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: "Title required" }]}
            >
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item
              name="body"
              label="Message"
              extra="Optional if the title or an image is enough. Current PCs still need a title so the popup can open."
            >
              <TextArea rows={4} maxLength={4000} />
            </Form.Item>
            <Form.Item name="urgency" label="Urgency">
              <Radio.Group>
                <Radio.Button value="info">Info</Radio.Button>
                <Radio.Button value="critical">Critical</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item
            name="displayMode"
            label="Display"
            extra="Toast is a short on-screen popup. An attached image is shown on that popup; current PCs use the acknowledge window so the picture is visible. Must acknowledge requires OK. Lock/idle uses a full-screen window on the logged-in desktop."
          >
              <Radio.Group>
                <Radio.Button value="toast">Toast</Radio.Button>
                <Radio.Button value="ack">Must acknowledge</Radio.Button>
                <Radio.Button value="lockscreen">Lock / idle takeover</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              label="Media (optional)"
              extra="The picture is downloaded to the PC and shown in the notification window. Videos open a link."
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space>
                  <Upload beforeUpload={uploadMedia} showUploadList={false} accept="image/*,video/*">
                    <Button icon={<UploadOutlined />}>Upload image or video</Button>
                  </Upload>
                  {media?.url ? (
                    <Button
                      onClick={() =>
                        setMedia({ type: "none", url: "", mime: "", fileName: "" })
                      }
                    >
                      Remove
                    </Button>
                  ) : null}
                </Space>
                {media?.url ? (
                  <span className="user-activity__muted">
                    Attached: {media.fileName || media.url} ({media.type})
                  </span>
                ) : null}
              </Space>
            </Form.Item>

            <Form.Item label="PC preview">
              <div
                style={{
                  border: "1px solid #d9d9d9",
                  borderRadius: 8,
                  minHeight: 280,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  background: previewMode === "lockscreen" ? "#141c28" : "#fff",
                  color: previewMode === "lockscreen" ? "#fff" : "inherit",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  {previewTitle || "Title"}
                </div>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    flex: 1,
                    marginBottom: 12,
                    opacity: 0.9,
                    minHeight: 48,
                  }}
                >
                  {previewBody || "Message"}
                </div>
                {media?.type === "image" && media?.url ? (
                  <img
                    src={assetUrl(media.url)}
                    alt={media.fileName || "Broadcast image"}
                    style={{
                      maxWidth: "100%",
                      maxHeight: 160,
                      objectFit: "contain",
                      borderRadius: 8,
                      background: previewMode === "lockscreen" ? "#1e293b" : "#f5f5f5",
                      margin: "0 auto 12px",
                      display: "block",
                    }}
                  />
                ) : null}
                <Button type="primary" style={{ alignSelf: "center" }}>
                  {previewMode === "toast" ? "OK" : "Acknowledge"}
                </Button>
              </div>
            </Form.Item>

            <Form.Item name={["survey", "enabled"]} label="Survey" valuePropName="checked">
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>
            {surveyEnabled ? (
              <>
                <Form.Item name={["survey", "question"]} label="Survey question">
                  <Input placeholder="Did you complete the training?" />
                </Form.Item>
                <Form.Item
                  name="surveyChoices"
                  label="Choices (comma-separated)"
                  initialValue="Yes, No"
                >
                  <Input placeholder="Yes, No" />
                </Form.Item>
              </>
            ) : null}

            <Form.Item
              name="targetType"
              label="Audience"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Radio value="all">All devices</Radio>
                <Radio value="group">Device groups</Radio>
                <Radio value="specific">Specific devices</Radio>
                <Radio value="region">Region</Radio>
              </Radio.Group>
            </Form.Item>
            {targetType === "group" && (
              <Form.Item
                name="groups"
                label="Groups"
                rules={[{ required: true, type: "array", min: 1, message: "Select groups" }]}
              >
                <SelectBox
                  className="w-100"
                  name="name"
                  endPoint="group-devices"
                  placeholder="Search device groups"
                  value={groupId}
                  multiple
                  onSelect={(value) => {
                    setGroupId(value)
                    form.setFieldsValue({ groups: value })
                  }}
                />
              </Form.Item>
            )}
            {targetType === "specific" && (
              <Form.Item
                name="devices"
                label="Devices"
                rules={[{ required: true, type: "array", min: 1, message: "Select devices" }]}
              >
                <SelectBox
                  className="w-100"
                  name="hostname"
                  multiple
                  endPoint="devices/identifiers"
                  placeholder="Search devices"
                  value={deviceIds}
                  onSelect={(value) => {
                    setDeviceIds(value)
                    form.setFieldsValue({ devices: value })
                  }}
                />
              </Form.Item>
            )}
            {targetType === "region" && (
              <Form.Item
                name="regions"
                label="Regions"
                rules={[{ required: true, message: "Select regions" }]}
              >
                <Select mode="multiple" options={REGIONS} />
              </Form.Item>
            )}
            {(targetType === "all" || targetType === "group" || targetType === "region") && (
              <Form.Item name="deviceStatuses" label="Device status filter">
                <Checkbox.Group
                  options={[
                    { label: "Online", value: "Online" },
                    { label: "Offline", value: "Offline" },
                  ]}
                />
              </Form.Item>
            )}

            <Form.Item
              name={["schedule", "mode"]}
              label="When"
              extra="Once later and Recurring can still be sent immediately with Send now and keep schedule."
            >
              <Radio.Group>
                <Radio.Button value="now">Send now</Radio.Button>
                <Radio.Button value="once">Once later</Radio.Button>
                <Radio.Button value="recurring">Recurring</Radio.Button>
              </Radio.Group>
            </Form.Item>
            {scheduleMode === "once" && (
              <Form.Item
                name={["schedule", "sendAt"]}
                label="Send at"
                rules={[{ required: true, message: "Pick a time" }]}
              >
                <DatePicker showTime style={{ width: "100%" }} />
              </Form.Item>
            )}
            {scheduleMode === "recurring" && (
              <>
                <CronScheduleFields form={form} namePath={["schedule", "cron"]} />
                <Form.Item name={["schedule", "timezone"]} label="Timezone">
                  <Select options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))} />
                </Form.Item>
              </>
            )}
            {scheduleMode === "once" && (
              <Form.Item name={["schedule", "timezone"]} label="Timezone" hidden>
                <Input />
              </Form.Item>
            )}
          </Form>
        ) : (
          <>
            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                minHeight: 240,
                padding: 16,
                display: "flex",
                flexDirection: "column",
                marginBottom: 16,
                background: detail?.displayMode === "lockscreen" ? "#141c28" : "#fff",
                color: detail?.displayMode === "lockscreen" ? "#fff" : "inherit",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                {detail?.title}
              </div>
              <p className="user-activity__muted" style={{ flex: 1, color: "inherit" }}>
                {detail?.body}
              </p>
              {detail?.media?.type === "image" && detail?.media?.url ? (
                <img
                  src={assetUrl(detail.media.url)}
                  alt={detail.media.fileName || "Broadcast image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 180,
                    objectFit: "contain",
                    borderRadius: 8,
                    margin: "0 auto 12px",
                    display: "block",
                    background:
                      detail?.displayMode === "lockscreen" ? "#1e293b" : "#f5f5f5",
                  }}
                />
              ) : null}
              <Button type="primary" style={{ alignSelf: "center" }}>
                {detail?.displayMode === "toast" ? "OK" : "Acknowledge"}
              </Button>
            </div>
            <p>
              Status: <strong>{detail?.status}</strong> · Mode:{" "}
              {detail?.displayMode} · Urgency: {detail?.urgency}
              {detail?.media?.type && detail.media.type !== "none"
                ? ` · Media: ${detail.media.type}`
                : ""}
              {` · ${scheduleLabel(detail)}`}
            </p>
            <p>
              Counts — targeted {detail?.counts?.targeted || 0}, delivered{" "}
              {detail?.counts?.delivered || 0}, acked {detail?.counts?.acked || 0},
              surveyed {detail?.counts?.surveyed || 0}, failed{" "}
              {detail?.counts?.failed || 0}
            </p>
            <h4>Deliveries</h4>
            <Table
              size="small"
              rowKey="_id"
              pagination={{ pageSize: 10 }}
              dataSource={detail?.deliveries || []}
              columns={[
                { title: "Host", dataIndex: "hostname" },
                { title: "Status", dataIndex: "status", width: 110 },
                {
                  title: "Acked",
                  dataIndex: "ackedAt",
                  width: 170,
                  render: (value) =>
                    value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—",
                },
                {
                  title: "Error",
                  dataIndex: "error",
                  ellipsis: true,
                },
              ]}
            />
            <h4 className="mt-3">Survey answers</h4>
            <Table
              size="small"
              rowKey="_id"
              pagination={{ pageSize: 10 }}
              dataSource={detail?.surveys || []}
              locale={{ emptyText: "No survey answers yet" }}
              columns={[
                { title: "Host", dataIndex: "hostname" },
                { title: "Choice", dataIndex: "choice" },
                {
                  title: "At",
                  dataIndex: "answeredAt",
                  render: (value) =>
                    value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—",
                },
              ]}
            />
          </>
        )}
      </Drawer>
      <Modal
        title={scheduleTarget ? `Schedule: ${scheduleTarget.title}` : "Schedule broadcast"}
        open={Boolean(scheduleTarget)}
        onCancel={() => setScheduleTarget(null)}
        onOk={saveSchedule}
        confirmLoading={loading}
        okText="Save schedule"
      >
        <Form form={scheduleForm} layout="vertical">
          <Form.Item name="mode" label="When" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="once">Once later</Radio.Button>
              <Radio.Button value="recurring">Recurring</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {scheduleModalMode === "once" && (
            <Form.Item
              name="sendAt"
              label="Send at"
              rules={[{ required: true, message: "Pick a time" }]}
            >
              <DatePicker showTime style={{ width: "100%" }} />
            </Form.Item>
          )}
          {scheduleModalMode === "recurring" && (
            <CronScheduleFields form={scheduleForm} namePath="cron" />
          )}
          <Form.Item name="timezone" label="Timezone">
            <Select options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))} />
          </Form.Item>
          <Form.Item name="sendNow" valuePropName="checked">
            <Checkbox>Also send now (keep this schedule)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </PanelLayout>
  )
}

export default DesktopBroadcasts
